import React, { useEffect, useState } from 'react';
import { useDrafts } from '../hooks/useDrafts';
import DraftTable from '../Components/Drafts/DraftTable';
import './Drafts.scss';
import { useAuth } from 'react-oidc-context';
import useUserRole from '../hooks/useUserRole';
import { Navigate } from 'react-router';
import DraftService from '../services/DraftService';
import ClearAllDraftsModal from '../Components/Modals/ClearAllDraftsModal';
import RemoveDraftModal from '../Components/Modals/RemoveDraftModal';
import { useNavigate } from 'react-router-dom';
import IFlightRangeData from '../types/FlightRanges';
import IReplacementRange from '../types/ReplacementRange';

const Drafts: React.FC = () => {
  const { draftData, isLoading, fetchDraftsError, fetchDrafts } = useDrafts();
  const auth = useAuth();
  const { isAdmin } = useUserRole(); // Get the admin status
  const [error, setError] = useState<string | null>(null);
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState<boolean>(false);
  const [clearAllSuccess, setClearAllSuccess] = useState<boolean>(false);
  const [isRemoveDraftModalOpen, setIsRemoveDraftModalOpen] = useState<boolean>(false);
  const [draftIdToRemove, setDraftIdToRemove] = useState<number>(0);
  const [removeDraftSuccess, setRemoveDraftSuccess] = useState<boolean>(false);

  const navigate = useNavigate();

  // Fetch draft data on component mount
  useEffect(() => {
    if (isAdmin) {
      fetchDrafts();
    }
  }, [isAdmin, fetchDrafts]);

  const handleClearAll = async () => {
    setIsClearAllModalOpen(true);
  };

  const convertDraftToFlightRanges = (draft: Array<IReplacementRange>): Array<IFlightRangeData & {
      operatingCarrierId?: string;
    }> => {
    return draft.map(range => ({
      id: range.id,
      marketingCarrier: range.marketingCarrier,
      operatingCarrier: range.operatingCarrier,
      flightTypeDescription: range.flightTypeDescription,
      effectiveDate: range.effectiveDate,
      discontinueDate: range.discontinueDate,
      modDate: '',
      modId: '',
      start: range.start,
      end: range.end
    }));
  }

  const handleEditDraft = (draftToEdit, draftId) => {
    const existingRanges: IReplacementRange[] = draftToEdit.filter(range => {
      return range.id > 0
    })

    const convertedExistingFlightRanges = convertDraftToFlightRanges(existingRanges);

    const enhancedExistingFlightRanges = convertedExistingFlightRanges.map(range => {
      // Get carrier ID
      const operatingCarrierId = range.operatingCarrierId?.toString();

      return {
        ...range,
        operatingCarrierId
      };
    });

    const replacementRanges: IReplacementRange[] = draftToEdit.filter(range => {
      return range.id === '0'
    })

    // navigate to modify view and pass on draft data
    navigate('/flight-ranges/admin/modify', {
      state: {
        selectedFlightRanges: enhancedExistingFlightRanges,
        replacementFlightRanges: replacementRanges,
        draftId: draftId
      }
    });
  };

  const handleRemoveDraft = (draftIdToRemove) => {
    setDraftIdToRemove(draftIdToRemove);
    setIsRemoveDraftModalOpen(true);
  };

  const clearAllDrafts = async () => {
    try {
      setError(null);
      const token = auth.user?.access_token;

      await DraftService.clearAll(token);

      setIsClearAllModalOpen(false);
      fetchDrafts(); // This will also refresh the distinct active carriers

      // Show success toast
      setClearAllSuccess(true);
      setTimeout(() => setClearAllSuccess(false), 10000);
    } catch (error: any) {
      setIsClearAllModalOpen(false);

      if (error?.response?.status === 400) {
        setError('Cannot clear all drafts: invalid request');
      } else {
        setError('Failed to clear all drafts');
      }
    }
  };  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/flight-ranges" replace />;
  }

  const removeDraft = async () => {
    try {
      setError(null);
      const token = auth.user?.access_token;

      await DraftService.remove(draftIdToRemove, token);

      setIsRemoveDraftModalOpen(false);
      fetchDrafts(); // This will also refresh the distinct active carriers

      // Show success toast
      setRemoveDraftSuccess(true);
      setTimeout(() => setRemoveDraftSuccess(false), 10000);
    } catch (error: any) {
      setIsRemoveDraftModalOpen(false);

      if (error?.response?.status === 400) {
        setError('Cannot remove draft: invalid request');
      } else if (error?.response?.status === 404) {
        setError('Cannot remove draft: draft ID not found');
      } else {
        setError('Failed to remove draft');
      }
    }
  };  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/flight-ranges" replace />;
  }

  // Error state rendering
  if (fetchDraftsError) {
    return (
      <div className="draft-view">
        <div 
          className="draft-view__error" 
          role="alert"
          aria-live="assertive"
        >
          <div className="error-icon" aria-hidden="true">⚠️</div>
          <div className="error-content">
            <h3>Unable to Load Drafts</h3>
            <p>{error}</p>
            <button 
              className="error-retry-button"
              onClick={fetchDrafts}
              type="button"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (draftData.length === 0 && !isLoading) {
    return (
      <div 
        className="draft-table__empty"
        role="status"
        aria-live="polite"
      >
        <p>You have no saved drafts</p>
      </div>
    );
  }

  return (
    <div className="draft-view">
      <div className="draft-view__container">
        <div className="draft-view__disclaimer">
          Drafts must be modified one at a time. Drafts are saved for 10 days and will expire at midnight PST on the 10th day.
        </div>
        <div className="draft-view__clear-all">
          <button
            className="draft-view__button-link"
            onClick={() => handleClearAll()}
            type="button"
          >
            Clear All
          </button>
        </div>
      </div>
      <div className="draft-view__content">
        <DraftTable 
          data={draftData}
          onEditDraft={handleEditDraft}
          onRemoveDraft={handleRemoveDraft}
        />
      </div>
      <ClearAllDraftsModal
        isOpen={isClearAllModalOpen}
        onClose={() => setIsClearAllModalOpen(false)}
        onConfirm={clearAllDrafts}
      />
      <RemoveDraftModal
        isOpen={isRemoveDraftModalOpen}
        onClose={() => setIsRemoveDraftModalOpen(false)}
        onConfirm={removeDraft}
      />
      <div className="partners-admin__toast">
        {clearAllSuccess && (
          <auro-toast
            style={{display: 'block', margin: '0.5rem 0'} as React.CSSProperties}
            variant="success"
            timeTilHide={10000}
            visible={true}
          >
            Clear all draft successfully.
          </auro-toast>
        )}
        {removeDraftSuccess && (
          <auro-toast
            style={{display: 'block', margin: '0.5rem 0'} as React.CSSProperties}
            variant="success"
            timeTilHide={10000}
            visible={true}
          >
            Remove draft successfully.
          </auro-toast>
        )}
      </div>
    </div>
  );
}

export default Drafts;
